// src/hooks/useInventory.ts
import { useState } from "react";
import {
    Inventory,
    InventoryCreationRequest,
    InventoryParameter,
    ItemLocation,
    FieldChange
} from "../types/Inventory";
import {
    createInventory as createInventoryApi,
    deleteInventoryById,
    deleteInventoryHtml as deleteInventoryHtmlApi,
    getInventoryById,
    patchInventory as patchInventoryApi,
    uploadInventoryHtml as uploadInventoryHtmlApi,
    uploadInventoryPhoto as uploadInventoryPhotoApi,
    deleteInventoryPhoto as deleteInventoryPhotoApi,
    reorderInventoryPhotos as reorderInventoryPhotosApi,
    setInventoryServiceArea as setInventoryServiceAreaApi,
    setInventoryItemLocation as setInventoryItemLocationApi,
} from "../api/InventoryAPI";
import type { CoverageArea } from "../utils/coverage";
import { logApiError, getUserMessage } from "../api/apiError";

export type PatchInventoryTask =
    | "AddParameter"
    | "RemoveParameter"
    | "EditParameterValue"
    | "UpdateFixedField";

interface UseInventoryOptions {
    token: string;
}

/**
 * Normalizes an inventory parameter into the wire payload: coerces the value by
 * data type and attaches selectionType / units where relevant. Shared by the
 * add and edit paths so both send identical, correctly-typed payloads.
 */
function buildParameterPayload(parameter: InventoryParameter) {
    let normalizedValue: any = parameter.value;

    switch (parameter.dataType) {
        case "BOOLEAN":
            normalizedValue = Boolean(parameter.value);
            break;

        case "INTEGER":
        case "DOUBLE":
            normalizedValue = Number(parameter.value);
            break;

        case "SELECTABLE":
            if (!Array.isArray(parameter.value)) {
                normalizedValue =
                    typeof parameter.value === "string" && parameter.value.trim()
                        ? parameter.value.split(",").map((v: string) => v.trim())
                        : [];
            }
            break;

        case "STRING":
        default:
            normalizedValue = String(parameter.value ?? "");
    }

    return {
        id: parameter.id,
        name: parameter.name,
        value: normalizedValue,
        dataType: parameter.dataType,
        ...(parameter.dataType === "SELECTABLE"
            ? { selectionType: (parameter as any).selectionType ?? "SINGLE" }
            : {}),
        // Numeric params carry the unit the value is stated in.
        // (Required for DOUBLE so the server can build the value map.)
        ...(parameter.dataType === "DOUBLE" || parameter.dataType === "INTEGER"
            ? { units: parameter.units }
            : {}),
    };
}

export function useInventory({ token }: UseInventoryOptions) {
    const [inventory, setInventory] = useState<Inventory | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const clearError = () => setError(null);

    // CREATE inventory (no parameters yet)
    const createInventory = async (payload: InventoryCreationRequest) => {
        // Don't create a blank listing: require at least a name, description, or
        // link. Throws so callers don't proceed as if the create succeeded.
        const hasContent = [payload.inventoryName, payload.description, payload.url]
            .some((v) => (v ?? "").trim().length > 0);
        if (!hasContent) {
            const msg = "A listing needs at least a name, description, or link.";
            setError(msg);
            throw new Error(msg);
        }
        setLoading(true);
        clearError();
        try {
            const created = await createInventoryApi(token, payload);
            setInventory(created);
            return created;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // CREATE inventory and apply everything collected on the create form:
    // parameter values, service-area coverage, and item location. Uses the
    // same patches (and server-side validation) as the post-save flow,
    // tracking the version locally between calls. If the create itself
    // fails this throws; if a later step fails the listing is kept, the
    // error is surfaced, and the rest still apply — the user fixes the
    // stragglers in the normal post-save sections.
    const createInventoryWithParameters = async (
        payload: InventoryCreationRequest,
        parameters: InventoryParameter[],
        coverage?: CoverageArea[] | null,
        location?: ItemLocation | null
    ) => {
        const hasContent = [payload.inventoryName, payload.description, payload.url]
            .some((v) => (v ?? "").trim().length > 0);
        if (!hasContent) {
            const msg = "A listing needs at least a name, description, or link.";
            setError(msg);
            throw new Error(msg);
        }
        setLoading(true);
        clearError();
        try {
            let current = await createInventoryApi(token, payload);
            const failures: string[] = [];
            const apply = async (label: string, body: Record<string, any>) => {
                try {
                    current = await patchInventoryApi(token, {
                        id: current.id,
                        version: Number(current.version),
                        ...body,
                    });
                } catch (err: any) {
                    logApiError(err);
                    failures.push(`${label}: ${getUserMessage(err)}`);
                }
            };
            for (const p of parameters) {
                await apply(p.name, { patchTask: "AddParameter", parameter: buildParameterPayload(p) });
            }
            if (coverage && coverage.length > 0) {
                await apply("service area", { patchTask: "SetServiceArea", coverage });
            }
            if (location) {
                await apply("location", { patchTask: "SetItemLocation", location });
            }
            setInventory(current);
            if (failures.length > 0) {
                setError("Listing created, but some of it was not saved — "
                    + failures.join("; "));
            }
            return current;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // LOAD existing inventory by id
    const loadInventory = async (id: string) => {
        setLoading(true);
        clearError();
        try {
            const found = await getInventoryById(token, id);
            setInventory(found);
            return found;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // UPDATE top-level inventory fields — one PATCH per field (backend takes fieldName + value at top level)
    const updateInventory = async (partial: Partial<Inventory>) => {
        if (!inventory || !inventory.id) return;
        if (Number.isNaN(Number(inventory.version))) {
            throw new Error("Inventory version is required for updates");
        }
        setLoading(true);
        clearError();
        try {
            const fields: { field: string; value: unknown }[] = [];
            if (partial.inventoryName !== undefined) fields.push({ field: "Name",       value: partial.inventoryName });
            if (partial.notes       !== undefined) fields.push({ field: "Notes",      value: partial.notes });
            if (partial.description !== undefined) fields.push({ field: "Description", value: partial.description });
            if (partial.url         !== undefined) fields.push({ field: "Url",         value: partial.url });
            if (partial.visibility  !== undefined) fields.push({ field: "Visibility",  value: partial.visibility });

            let updated: Inventory = inventory;
            for (const field of fields) {
                updated = await patchInventoryApi(token, {
                    id: updated.id,
                    version: Number(updated.version),
                    patchTask: "UpdateFixedField",
                    ...field,
                });
            }
            setInventory(updated);
            return updated;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // DELETE inventory
    const deleteInventory = async () => {
        if (!inventory || !inventory.id) return;
        setLoading(true);
        clearError();
        try {
            await deleteInventoryById(token, inventory.id);
            setInventory(null);
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // UPLOAD an HTML document and attach it to the current inventory.
    // The server bumps the version and rewrites the url, so we reload afterwards.
    const uploadHtml = async (file: File) => {
        if (!inventory || !inventory.id) {
            throw new Error("Save the inventory before attaching a document");
        }
        const version = Number(inventory.version);
        if (Number.isNaN(version)) {
            throw new Error("Invalid inventory version for upload");
        }
        setLoading(true);
        clearError();
        try {
            await uploadInventoryHtmlApi(token, inventory.id, version, file);
            const refreshed = await getInventoryById(token, inventory.id);
            setInventory(refreshed);
            return refreshed;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // REMOVE the attached HTML document. Returns the updated inventory.
    const removeHtml = async () => {
        if (!inventory || !inventory.id) return;
        const version = Number(inventory.version);
        if (Number.isNaN(version)) {
            throw new Error("Invalid inventory version for removal");
        }
        setLoading(true);
        clearError();
        try {
            const updated = await deleteInventoryHtmlApi(token, inventory.id, version);
            setInventory(updated);
            return updated;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // --- Photos (separate assets) ---
    // Each op bumps the version, so we reload the item afterwards.

    const uploadPhoto = async (file: File) => {
        if (!inventory || !inventory.id) throw new Error("Save the listing before adding photos");
        setLoading(true);
        clearError();
        try {
            await uploadInventoryPhotoApi(token, inventory.id, Number(inventory.version), file);
            const refreshed = await getInventoryById(token, inventory.id);
            setInventory(refreshed);
            return refreshed;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deletePhoto = async (photoId: string) => {
        if (!inventory || !inventory.id) return;
        setLoading(true);
        clearError();
        try {
            const updated = await deleteInventoryPhotoApi(token, inventory.id, photoId, Number(inventory.version));
            setInventory(updated);
            return updated;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const reorderPhotos = async (photoIdsInOrder: string[]) => {
        if (!inventory || !inventory.id) return;
        setLoading(true);
        clearError();
        try {
            const updated = await reorderInventoryPhotosApi(token, inventory.id, Number(inventory.version), photoIdsInOrder);
            setInventory(updated);
            return updated;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // --- Service area (coverage) ---

    const setServiceArea = async (coverage: CoverageArea[]) => {
        if (!inventory || !inventory.id) throw new Error("Save the listing before setting a service area");
        setLoading(true);
        clearError();
        try {
            const updated = await setInventoryServiceAreaApi(token, inventory.id, Number(inventory.version), coverage);
            setInventory(updated);
            return updated;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // --- Fixed item location (rentals, real estate) ---

    const setItemLocation = async (location: ItemLocation | null) => {
        if (!inventory || !inventory.id) throw new Error("Save the listing before setting its location");
        setLoading(true);
        clearError();
        try {
            const updated = await setInventoryItemLocationApi(token, inventory.id, Number(inventory.version), location);
            setInventory(updated);
            return updated;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // --- PATCH helpers ---

    const patchInventory = async (
        task: PatchInventoryTask,
        extra: Record<string, any> = {}
    ) => {
        if (!inventory || !inventory.id || inventory.version === undefined || inventory.version === null) {
            throw new Error("No inventory or version available for PATCH");
        }
        const version = Number(inventory.version);
        if (Number.isNaN(version)) {
            throw new Error("Invalid inventory version for PATCH");
        }

        const body = {
            id: inventory.id,
            version,
            patchTask: task,
            ...extra,
        };

        setLoading(true);
        clearError();

        try {
            const updated = await patchInventoryApi(token, body);
            setInventory(updated);
            return updated;
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const addParameter = async (parameter: InventoryParameter) => {
        if (!inventory) return;

        setLoading(true);
        setError(null);

        try {
            const version = Number(inventory.version);
            if (Number.isNaN(version)) {
                setError("Inventory version is required to add a parameter");
                return;
            }

            const body = {
                id: inventory.id,
                version,
                patchTask: "AddParameter",
                parameter: buildParameterPayload(parameter)
            };

            console.log("PATCH AddParameter body:", body);

            const updated = await patchInventoryApi(token, body);
            setInventory(updated);
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const changeParameterValue = async (parameter: InventoryParameter) => {
        return patchInventory("EditParameterValue", {
            parameter: buildParameterPayload(parameter)
        });
    };

    const removeParameter = async (name: string) => {
        return patchInventory("RemoveParameter", { parameterName:  name });
    };

    const changeDetails = async (changes: FieldChange[]) => {
        return patchInventory("UpdateFixedField", { changes });
    };

    return {
        inventory,
        setInventory,
        loading,
        error,
        clearError,
        createInventory,
        loadInventory,
        updateInventory,
        deleteInventory,
        addParameter,
        changeParameterValue,
        removeParameter,
        changeDetails,
        uploadHtml,
        removeHtml,
        uploadPhoto,
        deletePhoto,
        reorderPhotos,
        setServiceArea,
        setItemLocation,
        createInventoryWithParameters
    };
}